import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { appMonthYear } from '@/utils/appDateFormat';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { parseApiDateOnly } from '@/utils/apiDate';

interface PayslipForChart {
  basicSalary: number;
  variablePay: number;
  adjustments?: number;
  deductions: number;
  paymentDate: string;
  currencyCode?: string;
}

interface PayslipHistoryChartProps {
  payslips: PayslipForChart[];
  currencyLabel: string;
}

export default function PayslipHistoryChart({ payslips, currencyLabel }: PayslipHistoryChartProps) {
  const { t, i18n } = useTranslation('payroll');

  const chartData = useMemo(() => {
    const locale = i18n.language || undefined;
    return [...payslips]
      .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime())
      .slice(-12)
      .map((p) => {
        const gross = (p.basicSalary || 0) + (p.variablePay || 0) + (p.adjustments ?? 0);
        const deductions = p.deductions || 0;
        return {
          month: appMonthYear(parseApiDateOnly(p.paymentDate)),
          gross,
          deductions,
          netPay: gross - deductions,
        };
      });
  }, [payslips, i18n.language]);

  const formatCurrency = (value: number) => {
    const locale = i18n.language || undefined;
    return `${currencyLabel} ${value.toLocaleString(locale)}`;
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="mb-4 text-sm font-medium text-foreground">
        {t('payslips.earningsHistory')}
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData}>
          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip formatter={(value: number) => formatCurrency(value)} />
          <Legend />
          <Bar
            dataKey="gross"
            name={t('payslips.grossPay')}
            fill="var(--color-primary, #3b82f6)"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="deductions"
            name={t('payslips.deductions')}
            fill="var(--color-destructive, #ef4444)"
            radius={[4, 4, 0, 0]}
          />
          <Line
            dataKey="netPay"
            name={t('payslips.netPay')}
            stroke="var(--color-success, #22c55e)"
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
